import { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI, creativeAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PaintBrush, CircleNotch, Image, Palette, Download } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { AuthImage, downloadCreativeImage } from '../components/AuthImage';
import { creativeErrorMessage } from '../lib/creativeErrors';

// Soft-launch scope: brief -> one image -> download -> usage. Everything here is
// backed by a real endpoint; unsupported features are intentionally not shown.
const LANGUAGES = [
  { label: 'English', code: 'en' },
  { label: 'Hindi', code: 'hi' },
  { label: 'Arabic', code: 'ar' },
  { label: 'Tamil', code: 'ta' },
];
const FORMATS = [
  { label: 'Social post (1:1)', value: 'social_post' },
  { label: 'Banner (1200×628)', value: 'banner' },
  { label: 'Story (9:16)', value: 'story' },
  { label: 'Display ad (300×250)', value: 'display_ad' },
];
const OBJECTIVES = ['Awareness', 'Consideration', 'Conversion'];

let requestCounter = 0;
const newRequestId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `req-${Date.now()}-${++requestCounter}`);

// Phase 1 -- `embedded` renders inside the founder shell's Work -> Create
// (no second layout/navigation, the shell's own workspace instead of a
// picker). Standalone behaviour is unchanged.
export default function CreativeIntelligencePage({ embedded = false, workspaceId: embeddedWorkspaceId = '' } = {}) {
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState(embedded ? embeddedWorkspaceId : '');
  const [brief, setBrief] = useState({ product: '', objective: 'Conversion', message: '', language: 'en', format: 'social_post' });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState(null);
  const [brandKit, setBrandKit] = useState({ brand_name: '', primary_color: '#1A56DB', secondary_color: '#00D4AA', tone: '', style_notes: '' });
  const pendingRequest = useRef({ key: '', id: '' });

  useEffect(() => {
    if (embedded) return;
    clientsAPI.list().then((r) => { setClients(r.data); if (r.data.length > 0) setSel(r.data[0].id); }).catch(() => {});
  }, [embedded]);

  useEffect(() => {
    if (embedded) setSel(embeddedWorkspaceId);
  }, [embedded, embeddedWorkspaceId]);

  const Shell = embedded ? Fragment : DashboardLayout;

  const loadUsage = useCallback(() => {
    creativeAPI.usage().then((r) => setUsage(r.data)).catch(() => setUsage(null));
  }, []);
  const loadHistory = useCallback(() => {
    if (!sel) return;
    creativeAPI.list(sel).then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, [sel]);

  useEffect(() => { loadUsage(); }, [loadUsage]);
  useEffect(() => {
    if (!sel) return;
    setResult(null);
    loadHistory();
    creativeAPI.brandKit(sel).then((r) => setBrandKit((prev) => ({ ...prev, ...r.data }))).catch(() => {});
  }, [sel, loadHistory]);

  const cost = usage?.image_cost;
  const disabled = usage && usage.enabled === false;
  const insufficient = usage && cost != null && usage.remaining < cost;
  const canGenerate = !!usage && !disabled && !insufficient;

  const handleGenerate = async () => {
    setGenerating(true);
    setResult(null);
    const prompt = `${brief.product}: ${brief.message}. Campaign objective: ${brief.objective}.`;
    const payload = { client_id: sel, prompt, format: brief.format, industry: '', language: brief.language };
    // A retry of the SAME brief reuses the same request id, so it can never be charged twice.
    const key = JSON.stringify(payload);
    if (pendingRequest.current.key !== key) pendingRequest.current = { key, id: newRequestId() };
    try {
      const res = await creativeAPI.generate({ ...payload, request_id: pendingRequest.current.id });
      setResult(res.data);
      pendingRequest.current = { key: '', id: '' };
      toast.success('Image generated');
      loadUsage();
      loadHistory();
    } catch (err) {
      toast.error(creativeErrorMessage(err));
      loadUsage();
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveBrandKit = async () => {
    try {
      await creativeAPI.saveBrandKit({ ...brandKit, client_id: sel });
      toast.success('Brand kit saved');
    } catch { toast.error('Could not save the brand kit'); }
  };

  const handleDownload = async (imageId) => {
    try { await downloadCreativeImage(imageId, `martian-creative-${imageId.slice(0, 8)}.png`); }
    catch { toast.error('Download failed'); }
  };

  return (
    <Shell>
      <div className="space-y-5" data-testid="creative-intelligence-page">
        {!embedded && <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl font-black text-[var(--text-primary)] tracking-tight">Creative Intelligence</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Turn a brief into an ad image for your brand.</p>
          </div>
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
              {clients.map((c) => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{c.display_name || c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>}

        {/* Day 2.5 -- Creative Credits display: visual container only,
             zero change to the values/conditions rendered inside. Uses
             the "inset" tone deliberately (a metered/consumable balance
             reads as something to look INTO, not a decorative highlight
             card) -- never "raised", so this never looks more finished
             or more available than the real remaining/cost numbers say. */}
        <div data-testid="creative-usage" className="rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-inset)] shadow-[var(--shadow-inset)] p-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">Creative Credits</p>
            <p className="text-lg font-bold font-mono text-[var(--text-primary)]" data-testid="creative-credits-remaining">
              {usage ? `${usage.remaining} available` : '—'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">Generate image</p>
            <p className="text-lg font-bold font-mono text-[var(--text-primary)]" data-testid="creative-generation-cost">
              {cost != null ? `${cost} Creative Credits` : '—'}
            </p>
          </div>
        </div>

        <Tabs defaultValue="generate" className="space-y-4">
          <TabsList className="bg-[var(--surface)] border border-[var(--border)] overflow-x-auto">
            <TabsTrigger value="generate" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white whitespace-nowrap"><PaintBrush size={12} className="mr-1" /> Generate</TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white whitespace-nowrap"><Image size={12} className="mr-1" /> History</TabsTrigger>
            <TabsTrigger value="brandkit" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white whitespace-nowrap"><Palette size={12} className="mr-1" /> Brand Kit</TabsTrigger>
          </TabsList>

          <TabsContent value="generate" className="space-y-4">
            <div className="rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-raised)] shadow-[var(--shadow-raised)] p-6">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Creative brief</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-[9px] text-[var(--text-tertiary)] mb-1 font-mono uppercase">Product name</label>
                    <Input value={brief.product} onChange={(e) => setBrief({ ...brief, product: e.target.value })} placeholder="Product name" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[11px]" /></div>
                  <div><label className="block text-[9px] text-[var(--text-tertiary)] mb-1 font-mono uppercase">Objective</label>
                    <Select value={brief.objective} onValueChange={(v) => setBrief({ ...brief, objective: v })}>
                      <SelectTrigger className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[11px]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">{OBJECTIVES.map((o) => <SelectItem key={o} value={o} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{o}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div><label className="block text-[9px] text-[var(--text-tertiary)] mb-1 font-mono uppercase">Key message (150 chars max)</label>
                  <Input value={brief.message} onChange={(e) => setBrief({ ...brief, message: e.target.value.slice(0, 150) })} placeholder="One sentence: what should the customer know or feel?" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[11px]" />
                  <p className="text-[8px] text-[var(--text-tertiary)] text-right mt-0.5">{brief.message.length}/150</p></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-[9px] text-[var(--text-tertiary)] mb-1 font-mono uppercase">Language of any text in the image</label>
                    <Select value={brief.language} onValueChange={(v) => setBrief({ ...brief, language: v })}>
                      <SelectTrigger className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[11px]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">{LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{l.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><label className="block text-[9px] text-[var(--text-tertiary)] mb-1 font-mono uppercase">Format</label>
                    <Select value={brief.format} onValueChange={(v) => setBrief({ ...brief, format: v })}>
                      <SelectTrigger className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[11px]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">{FORMATS.map((f) => <SelectItem key={f.value} value={f.value} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{f.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                {usage && (
                  <div data-testid="creative-cost-confirmation" className="rounded border border-[var(--border)] bg-[var(--surface-sunken)] px-3 py-2 text-[11px] text-[var(--text-secondary)]">
                    <p className="font-semibold text-[var(--text-primary)]">Generate image</p>
                    <p>Cost: {cost} Creative Credits</p>
                    <p>Remaining after generation: {Math.max(0, usage.remaining_after ?? usage.remaining - cost)}</p>
                  </div>
                )}
                {insufficient && !disabled && <p data-testid="creative-insufficient" className="text-[11px] text-[var(--error)]">Insufficient Creative Credits</p>}
                {disabled && <p data-testid="creative-disabled" className="text-[11px] text-[var(--error)]">Creative generation is currently unavailable.</p>}
                <Button onClick={handleGenerate} disabled={generating || !brief.product || !sel || !canGenerate} className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white h-10" data-testid="creative-generate">
                  {generating ? <><CircleNotch size={16} className="animate-spin mr-2" />Generating…</> : <><PaintBrush size={16} className="mr-2" />Generate image{cost != null ? ` · ${cost} Creative Credits` : ''}</>}
                </Button>
              </div>
            </div>
            {result?.images?.length > 0 && (
              <div className="rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-raised)] shadow-[var(--shadow-raised)] p-6 animate-fade-in-up" data-testid="creative-result">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-[var(--text-primary)]">Your image</h3>
                  <span className="text-[9px] font-mono text-[var(--text-tertiary)]">{result.replayed ? 'Already generated — no new charge' : `${result.credits_charged ?? ''} Creative Credits used`}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {result.images.map((img) => (
                    <div key={img.id} className="rounded border border-[var(--border)] overflow-hidden">
                      <AuthImage imageId={img.id} alt="Generated creative" className="w-full max-h-[420px] object-contain bg-[var(--surface-sunken)]" />
                      <div className="p-2 flex justify-end">
                        <Button size="sm" variant="outline" onClick={() => handleDownload(img.id)} className="border-[var(--border)] text-[var(--text-secondary)] text-[10px]"><Download size={12} className="mr-1" />Download</Button>
                      </div>
                    </div>
                  ))}
                </div>
                {result.ai_notes && <p className="mt-3 text-[11px] text-[var(--text-secondary)]">{result.ai_notes}</p>}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-3">
            {history && history.length === 0 && (
              <div className="rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-inset)] shadow-[var(--shadow-inset)] text-center py-8"><Image size={32} className="mx-auto mb-2 text-[var(--text-tertiary)]" /><p className="text-xs text-[var(--text-secondary)]">No images generated for this brand yet.</p></div>
            )}
            {history && history.length > 0 && (
              <div className="rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-raised)] shadow-[var(--shadow-raised)] p-6 overflow-x-auto" data-testid="creative-history">
                <table className="w-full text-left text-[11px]">
                  <thead>
                    <tr className="text-[9px] font-mono uppercase text-[var(--text-tertiary)] border-b border-[var(--border)]">
                      <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Asset</th><th className="py-2 pr-3">Type</th>
                      <th className="py-2 pr-3">Creative Credits used</th><th className="py-2 pr-3">Status</th><th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((c) => (
                      <tr key={c.id} className="border-b border-[var(--border-light)] align-middle" data-testid="creative-history-row">
                        <td className="py-2 pr-3 font-mono text-[var(--text-tertiary)] whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-[var(--surface-sunken)]">
                              {c.images?.[0] ? <AuthImage imageId={c.images[0].id} alt="" className="w-full h-full object-cover" /> : <Image size={16} className="m-auto mt-4 text-[var(--text-tertiary)]" />}
                            </div>
                            <span className="text-[var(--text-primary)] truncate max-w-[220px]">{c.prompt || 'Image generation'}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3 capitalize">{c.type || 'image'}</td>
                        <td className="py-2 pr-3 font-mono">{c.credits_used ?? 0}</td>
                        <td className="py-2 pr-3">{c.status === 'failed' ? 'Failed — not charged' : 'Generated'}</td>
                        <td className="py-2 text-right">
                          {c.images?.[0] && (
                            <Button size="sm" variant="outline" onClick={() => handleDownload(c.images[0].id)} className="border-[var(--border)] text-[var(--text-secondary)] text-[10px]"><Download size={12} className="mr-1" />Download</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="brandkit" className="space-y-4">
            <div className="rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-raised)] shadow-[var(--shadow-raised)] p-6">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4"><Palette size={16} className="inline mr-2 text-[var(--accent)]" weight="duotone" />Brand kit</h3>
              <p className="text-[11px] text-[var(--text-secondary)] mb-3">Used to guide every image generated for this brand.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-[9px] text-[var(--text-tertiary)] mb-1 font-mono uppercase">Brand name</label>
                  <Input value={brandKit.brand_name || ''} onChange={(e) => setBrandKit({ ...brandKit, brand_name: e.target.value })} className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="block text-[9px] text-[var(--text-tertiary)] mb-1 font-mono uppercase">Primary colour</label>
                    <div className="flex gap-1"><input type="color" value={brandKit.primary_color || '#1A56DB'} onChange={(e) => setBrandKit({ ...brandKit, primary_color: e.target.value })} className="w-8 h-8 rounded" /><Input value={brandKit.primary_color || ''} onChange={(e) => setBrandKit({ ...brandKit, primary_color: e.target.value })} className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] font-mono text-[10px]" /></div></div>
                  <div><label className="block text-[9px] text-[var(--text-tertiary)] mb-1 font-mono uppercase">Secondary</label>
                    <div className="flex gap-1"><input type="color" value={brandKit.secondary_color || '#00D4AA'} onChange={(e) => setBrandKit({ ...brandKit, secondary_color: e.target.value })} className="w-8 h-8 rounded" /><Input value={brandKit.secondary_color || ''} onChange={(e) => setBrandKit({ ...brandKit, secondary_color: e.target.value })} className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] font-mono text-[10px]" /></div></div>
                </div>
                <div className="md:col-span-2"><label className="block text-[9px] text-[var(--text-tertiary)] mb-1 font-mono uppercase">Tone of voice</label>
                  <textarea value={brandKit.tone || ''} onChange={(e) => setBrandKit({ ...brandKit, tone: e.target.value })} rows={2} placeholder="E.g. Direct and warm. Never corporate." className="w-full bg-[var(--surface-sunken)] border border-[var(--border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-[11px] placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent)] focus:outline-none resize-none" /></div>
                <div className="md:col-span-2"><label className="block text-[9px] text-[var(--text-tertiary)] mb-1 font-mono uppercase">Style notes</label>
                  <textarea value={brandKit.style_notes || ''} onChange={(e) => setBrandKit({ ...brandKit, style_notes: e.target.value })} rows={2} placeholder="E.g. Clean layouts, natural light, lots of white space." className="w-full bg-[var(--surface-sunken)] border border-[var(--border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-[11px] placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent)] focus:outline-none resize-none" /></div>
              </div>
              <Button onClick={handleSaveBrandKit} className="mt-4 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">Save brand kit</Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Shell>
  );
}
